/**
 * Atlas de tiles. No v2 o atlas vem junto com o mapa (`/mapas/{id}-atlas.png`);
 * o motor pede um id e recebe o retângulo — não calcula índice.
 */

export interface TileDef {
  readonly id: number;
  readonly nome: string;
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
  readonly ancoraX: number;
  readonly ancoraY: number;
  readonly familia?: string;
  readonly andavel?: boolean;
  readonly bloqueia?: boolean;
  readonly altura?: number;
  /** Fonte de luz (techspec-dia-e-noite): o id da textura no `light.mul`, a cor e se tremula (fogo). */
  readonly luz?: number;
  readonly cor?: string;
  readonly tremula?: boolean;
  /** Item animado: os defs de cada quadro (o `animdata.mul` da UO) e quanto dura cada um. */
  readonly quadros?: readonly number[];
  readonly passoMs?: number;
}

export interface Tileset {
  readonly nome: string;
  readonly imagem: HTMLCanvasElement;
  /**
   * Id inexistente: devolve o tile 0 e avisa. Índice fora da faixa é dado
   * corrompido, mas não pode virar laço nem tela preta (A10).
   */
  tile(id: number): TileDef;
}

const ATLAS_OK = /^[a-z0-9-]+\.png$/;

export function validarNomeAtlas(nome: string): string {
  if (!ATLAS_OK.test(nome))
    throw new Error(`Atlas recusado: "${nome}". Só [a-z0-9-]+.png dentro de /mapas/.`);
  return nome;
}

function rasterizar(img: HTMLImageElement): HTMLCanvasElement {
  const cv = document.createElement('canvas');
  cv.width = img.naturalWidth || img.width;
  cv.height = img.naturalHeight || img.height;
  const g = cv.getContext('2d');
  if (!g) throw new Error('Atlas sem contexto 2D.');
  g.drawImage(img, 0, 0);
  return cv;
}

export function carregarImagem(url: string, sinal?: AbortSignal): Promise<HTMLCanvasElement> {
  return new Promise((ok, err) => {
    const img = new Image();
    const abortar = () => {
      img.src = '';
      err(new DOMException('Carregamento do atlas cancelado.', 'AbortError'));
    };
    if (sinal?.aborted) {
      abortar();
      return;
    }
    sinal?.addEventListener('abort', abortar, { once: true });
    img.onload = () => {
      const pronto = typeof img.decode === 'function' ? img.decode() : Promise.resolve();
      void pronto.then(() => ok(rasterizar(img))).catch(() => {
        try { ok(rasterizar(img)); }
        catch (e) { err(e instanceof Error ? e : new Error(`Não deu para abrir o atlas: ${url}`)); }
      });
    };
    img.onerror = () => err(new Error(`Não deu para abrir o atlas: ${url}`));
    img.src = url;
  });
}

export function montarTileset(
  nome: string,
  imagem: HTMLCanvasElement,
  tiles: readonly TileDef[]
): Tileset {
  const porId = new Map<number, TileDef>();
  for (const t of tiles) porId.set(t.id, t);
  const zero = porId.get(0);
  return {
    nome,
    imagem,
    tile(id: number) {
      const def = porId.get(id);
      if (def) return def;
      console.warn(`Tileset ${nome}: tile ${id} não existe; usando 0.`);
      if (zero) return zero;
      throw new Error(`Tileset ${nome}: tile ${id} não existe e não há tile 0.`);
    }
  };
}
